import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Play, Printer, Trash2, Eye, CheckCircle, Pencil } from 'lucide-react';
import { api } from '../../services/api';
import { legacyApi } from '../../services/legacyApi';
import { WorkOrder, BOM, Item } from '../../services/types';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { WorkOrderModal } from './WorkOrderModal';
import { BOMEditor } from './BOMEditor';
import { CompletionModal, type CompletionSummary } from './CompletionModal';
import { ProductionTracking } from './ProductionTracking';
import { DailyShiftEntry } from './DailyShiftEntry';
import { MaterialIssueBoard } from './MaterialIssueBoard';
import { CompactTable } from '../../components/ui/CompactTable';
import { Button } from '../../components/ui/button';
import { TableFilterBar } from '../../components/table-filters/TableFilterBar';
import { useTableFilterState } from '../../hooks/useTableFilterState';
import { FlowEntryBanner } from '../../components/workflow/FlowEntryBanner';
import { useConfirm } from '../../hooks/use-confirm';
import { StandardActions } from '../../modules/legacy-core/components/StandardActions';
import {
    getWorkOrderDescriptionLines,
    getWorkOrderDescriptionSummary,
    getOperatorWorkOrderLabel,
    getWorkOrderSearchText,
} from './workOrderDisplay';

export function ManufacturingDashboard() {
    const navigate = useNavigate();
    const { confirm } = useConfirm();
    const [searchParams] = useSearchParams();
    const tabFromUrl = searchParams.get('tab');
    const validTabs = ['orders', 'boms', 'tracking', 'shift-entry', 'material-issue'] as const;
    const initialTab = validTabs.includes(tabFromUrl as any) ? (tabFromUrl as 'orders' | 'boms' | 'tracking' | 'shift-entry' | 'material-issue') : 'orders';
    const [activeTab, setActiveTab] = useState<'orders' | 'boms' | 'tracking' | 'shift-entry' | 'material-issue'>(initialTab);
    const [orders, setOrders] = useState<WorkOrder[]>([]);
    const [boms, setBoms] = useState<BOM[]>([]);
    const [items, setItems] = useState<Item[]>([]);
    const [isWOModalOpen, setIsWOModalOpen] = useState(false);
    const [isBOMModalOpen, setIsBOMModalOpen] = useState(false);
    const [selectedRecipe, setSelectedRecipe] = useState<BOM | null>(null);
    const [recipeModalMode, setRecipeModalMode] = useState<'create' | 'view' | 'edit'>('create');
    const [completionTargetId, setCompletionTargetId] = useState<number | null>(null);
    const [actionBusyKey, setActionBusyKey] = useState<string | null>(null);
    const { state, setFilter, isDirty } = useTableFilterState({ q_orders: '', q_boms: '' });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [woList, bomList, itemList] = await Promise.all([
                api.manufacturing.listWorkOrders(),
                api.manufacturing.listBoms(),
                api.inventory.list()
            ]);
            setOrders(Array.isArray(woList) ? woList : []);
            setBoms(Array.isArray(bomList) ? bomList : []);
            setItems(Array.isArray(itemList) ? itemList : []);
        } catch (e: any) {
            setOrders([]);
            setBoms([]);
            setItems([]);
            toast.error(e?.message || 'Failed to load manufacturing data');
        }
    };

    const handleStart = async (id: number) => {
        const key = `start:${id}`;
        if (actionBusyKey === key) return;
        try {
            setActionBusyKey(key);
            const precheck = await legacyApi.workflow.precheck({
                entity: 'WORK_ORDER',
                entity_id: id,
                action: 'start',
            });
            const status = String(precheck.overall_status || 'pass').toLowerCase();
            if (status === 'fail') {
                toast.error(precheck.checks?.find((check) => String(check.status).toLowerCase() === 'fail')?.message || 'Precheck failed');
                return;
            }
            if (status === 'warn') {
                const proceed = await confirm('Start production with warnings?', {
                    description: precheck.checks?.map((check) => `- ${check.message}`).join('\n') || 'Review precheck warnings.',
                    confirmText: 'Start Anyway',
                    cancelText: 'Cancel',
                });
                if (!proceed) return;
            }
            await api.manufacturing.startWorkOrder(id);
            await loadData();
        } catch (e: any) {
            toast.error(e.message || 'Failed to start work order');
        } finally {
            setActionBusyKey(null);
        }
    };

    const handleCompleteClick = (id: number) => {
        setCompletionTargetId(id);
    };

    const handlePrint = (id: number) => {
        try {
            api.manufacturing.openWorkOrderPrint(id, true);
        } catch (e: any) {
            toast.error(e?.message || 'Failed to open work order print');
        }
    };

    const handleDelete = async (id: number) => {
        const key = `delete:${id}`;
        if (actionBusyKey === key) return;
        const ok = await confirm(`Delete WO-${id}?`, {
            description: 'Only Draft/Planned work orders can be deleted.',
            confirmText: 'Delete',
            cancelText: 'Cancel',
        });
        if (!ok) return;
        try {
            setActionBusyKey(key);
            await api.manufacturing.deleteWorkOrder(id);
            toast.success('Work order deleted');
            await loadData();
        } catch (e: any) {
            toast.error(e?.message || 'Failed to delete work order');
        } finally {
            setActionBusyKey(null);
        }
    };

    const confirmCompletion = async (costs: { labor_cost: number; overhead_cost: number }) => {
        if (!completionTargetId) return;
        const key = `complete:${completionTargetId}`;
        if (actionBusyKey === key) return;
        try {
            setActionBusyKey(key);
            const precheck = await legacyApi.workflow.precheck({
                entity: 'WORK_ORDER',
                entity_id: completionTargetId,
                action: 'complete',
            });
            const status = String(precheck.overall_status || 'pass').toLowerCase();
            if (status === 'fail') {
                toast.error(precheck.checks?.find((check) => String(check.status).toLowerCase() === 'fail')?.message || 'Precheck failed');
                return;
            }
            if (status === 'warn') {
                const proceed = await confirm('Complete work order with warnings?', {
                    description: precheck.checks?.map((check) => `- ${check.message}`).join('\n') || 'Review precheck warnings.',
                    confirmText: 'Complete Anyway',
                    cancelText: 'Cancel',
                });
                if (!proceed) return;
            }
            await api.manufacturing.completeWorkOrder(completionTargetId, costs);
            await loadData();
            setCompletionTargetId(null);
        } catch (e: any) {
            toast.error(e.message || 'Failed to complete work order');
        } finally {
            setActionBusyKey(null);
        }
    };

    const getItemName = (id?: number | null) => {
        if (!id) return 'Optional / not linked';
        const item = items.find(i => Number(i.id) === Number(id));
        return item ? `${item.code} - ${item.name}` : `Item ${id}`;
    };
    const getRecipeName = (bom: BOM) => bom.bom_name || `Recipe ${bom.id}`;
    const openRecipeModal = (bom: BOM | null, mode: 'create' | 'view' | 'edit') => {
        setSelectedRecipe(bom);
        setRecipeModalMode(mode);
        setIsBOMModalOpen(true);
    };
    const completionTargetOrder = completionTargetId !== null ? orders.find((order) => order.wo_id === completionTargetId) : null;
    const completionSummary: CompletionSummary | null = completionTargetOrder ? {
        target_output_qty: Number(completionTargetOrder.quantity || 0),
        produced_output_qty: Number(completionTargetOrder.produced_qty || 0),
        remaining_output_qty: Number(completionTargetOrder.remaining_qty || 0),
        progress_percent: Number(completionTargetOrder.progress_percent || 0),
        current_total_cost: Number(completionTargetOrder.total_cost || 0),
        labor_hours_total: Number(completionTargetOrder.labor_hours_total || 0),
        labor_cost_logged: Number(completionTargetOrder.cost_breakdown_summary?.labor_cost_total || completionTargetOrder.labor_cost || 0),
        overhead_cost_logged: Number(completionTargetOrder.cost_breakdown_summary?.overhead_cost_total || completionTargetOrder.overhead_cost || 0),
        extra_cost_logged: Number(completionTargetOrder.cost_breakdown_summary?.extra_cost_total || 0),
        issue_cost_total: Number(completionTargetOrder.cost_breakdown_summary?.issue_cost_total || 0),
    } : null;

    // Column Definitions
    const woColumns = [
        { header: 'ID', accessor: (w: WorkOrder) => `#${w.wo_id}`, className: 'font-bold w-16' },
        {
            header: 'Work Order',
            accessor: (w: WorkOrder) => {
                return (
                    <div className="min-w-[160px] space-y-1">
                        <div className="font-medium text-slate-900">{getOperatorWorkOrderLabel(w)}</div>
                        <div className="text-xs text-slate-500">Operational production job</div>
                        <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                            WO
                        </span>
                    </div>
                );
            }
        },
        {
            header: 'Project / Description',
            accessor: (w: WorkOrder) => {
                const lines = getWorkOrderDescriptionLines(w);
                return (
                    <div className="min-w-[260px] max-w-[420px]">
                        <div className="font-medium text-slate-900 truncate" title={getWorkOrderDescriptionSummary(w)}>
                            {getWorkOrderDescriptionSummary(w)}
                        </div>
                        {lines.length > 1 ? (
                            <div className="text-xs text-slate-500 truncate" title={lines.slice(1).join(' • ')}>
                                Also: {lines.slice(1, 3).join(' • ')}
                                {lines.length > 3 ? ` + ${lines.length - 3} more` : ''}
                            </div>
                        ) : null}
                    </div>
                );
            },
        },
        { header: 'Qty', accessor: (w: WorkOrder) => (w.quantity || 0).toFixed(2), className: 'text-right' },
        { header: 'Hours', accessor: (w: WorkOrder) => Number(w.labor_hours_total || 0).toFixed(2), className: 'text-right' },
        {
            header: 'Sub-parts',
            accessor: (w: WorkOrder) => (
                <div className="text-xs text-slate-600 min-w-[120px]">
                    <div className="font-medium text-slate-800">{Number(w.child_count || 0)} total</div>
                    <div>{Number(w.active_child_count || 0)} active | {Number(w.pending_child_count || 0)} pending</div>
                </div>
            ),
        },
        {
            header: 'WO Completion',
            accessor: (w: WorkOrder) => {
                const pct = Math.max(0, Math.min(100, Number(w.progress_percent || 0)));
                const produced = Number(w.produced_qty || 0);
                const target = Number(w.quantity || 0);
                return (
                    <div className="min-w-[170px]">
                        <div className="h-2 w-full bg-slate-100 rounded overflow-hidden">
                            <div className="h-full bg-blue-500" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="text-[11px] text-slate-600 mt-1">
                            {produced.toFixed(2)} / {target.toFixed(2)} ({pct.toFixed(0)}%)
                        </div>
                    </div>
                );
            }
        },
        {
            header: 'Status', accessor: (w: WorkOrder) => (
                <span className={cn(
                    "px-2 py-1 rounded-full text-xs font-medium",
                    w.status === 'Draft' || w.status === 'Planned' ? "bg-secondary text-secondary-foreground" :
                        w.status === 'In-Progress' || w.status === 'InProgress' ? "bg-blue-100 text-blue-700" :
                            "bg-green-100 text-green-700"
                )}>
                    {w.status}
                </span>
            )
        },
        {
            header: 'Actions', accessor: (w: WorkOrder) => (
                <StandardActions
                    view={{
                        title: 'View Work Order',
                        onClick: (event) => {
                            event.stopPropagation();
                            navigate(`/manufacturing/orders/${w.wo_id}`);
                        },
                        icon: <Eye className="w-4 h-4 text-green-600" />,
                    }}
                    download={{
                        title: 'Print Work Order',
                        onClick: (event) => {
                            event.stopPropagation();
                            handlePrint(w.wo_id);
                        },
                        icon: <Printer className="w-4 h-4 text-purple-600" />,
                    }}
                    remove={(w.status === 'Planned' || w.status === 'Draft') ? {
                        title: actionBusyKey === `delete:${w.wo_id}` ? 'Deleting work order' : 'Delete Work Order',
                        onClick: (event) => {
                            event.stopPropagation();
                            void handleDelete(w.wo_id);
                        },
                        icon: <Trash2 className="w-4 h-4 text-red-600" />,
                        disabled: actionBusyKey === `delete:${w.wo_id}`,
                    } : undefined}
                    overflow={[
                        ...(w.status === 'Planned' || w.status === 'Draft'
                            ? [{
                                label: actionBusyKey === `start:${w.wo_id}` ? 'Starting...' : 'Start',
                                onClick: (event: React.MouseEvent) => {
                                    event.stopPropagation();
                                    void handleStart(w.wo_id);
                                },
                                icon: <Play className="w-4 h-4 text-blue-600" />,
                                disabled: actionBusyKey === `start:${w.wo_id}`,
                            }]
                            : []),
                        ...((w.status === 'InProgress' || w.status === 'In-Progress')
                            ? [{
                                label: 'Complete',
                                onClick: (event: React.MouseEvent) => {
                                    event.stopPropagation();
                                    handleCompleteClick(w.wo_id);
                                },
                                icon: <CheckCircle className="w-4 h-4 text-emerald-600" />,
                                disabled: actionBusyKey === `complete:${w.wo_id}`,
                            }]
                            : []),
                    ]}
                />
            )
        }
    ];

    const bomColumns = [
        {
            header: 'Recipe Name',
            accessor: (b: BOM) => (
                <div className="min-w-[220px]">
                    <div className="font-medium text-slate-900">{getRecipeName(b)}</div>
                    <div className="text-xs text-slate-500">Recipe #{b.id} · Rev {Number(b.revision_no || 1)}</div>
                </div>
            ),
        },
        { header: 'FG Reference', accessor: (b: BOM) => getItemName(b.finished_good_id) },
        {
            header: 'Summary',
            accessor: (b: BOM) => {
                const parentCount = (b.components || []).filter((c) => !c.recipe_line_id).length;
                const childCount = (b.structure_lines || []).length;
                return (
                    <div className="text-xs text-slate-600">
                        <div>{parentCount} parent material(s)</div>
                        <div>{childCount} sub-part(s) · {(b.components || []).length} total material(s)</div>
                    </div>
                );
            },
        },
        {
            header: 'Structure', accessor: (b: BOM) => (
                <div className="space-y-1 text-xs">
                    <div className="flex flex-wrap gap-1">
                        {b.components.filter((c) => !c.recipe_line_id).map((c, i) => (
                            <span key={`parent-${i}`} className="bg-muted border px-2 py-0.5 rounded">
                                {getItemName(c.item_id)}: {c.quantity}
                            </span>
                        ))}
                    </div>
                    {(b.structure_lines || []).map((line) => (
                        <div key={line.recipe_line_id || line.description} className="text-slate-600">
                            <span className="font-medium">{line.description}</span>
                            <span> x {line.quantity_per_parent}</span>
                            <span className="ml-2">
                                {(b.components || []).filter((c) => Number(c.recipe_line_id || 0) === Number(line.recipe_line_id || 0)).length} material(s)
                            </span>
                        </div>
                    ))}
                </div>
            )
        },
        {
            header: 'Actions',
            accessor: (b: BOM) => (
                <StandardActions
                    view={{
                        title: 'View Recipe',
                        onClick: (event) => {
                            event.stopPropagation();
                            openRecipeModal(b, 'view');
                        },
                        icon: <Eye className="w-4 h-4 text-green-600" />,
                    }}
                    edit={{
                        title: 'Edit / New Revision',
                        onClick: (event) => {
                            event.stopPropagation();
                            openRecipeModal(b, 'edit');
                        },
                        icon: <Pencil className="w-4 h-4 text-blue-600" />,
                    }}
                />
            ),
        }
    ];
    const filteredOrders = useMemo(() => {
        const query = state.q_orders.trim().toLowerCase();
        if (!query) return orders;
        return orders.filter((w) =>
            String(w.wo_id).includes(query) ||
            getWorkOrderSearchText(w).includes(query) ||
            getOperatorWorkOrderLabel(w).toLowerCase().includes(query)
        );
    }, [orders, state.q_orders]);
    const filteredBoms = useMemo(() => {
        const query = state.q_boms.trim().toLowerCase();
        if (!query) return boms;
        return boms.filter((b) =>
            String(b.id).includes(query) ||
            String(getRecipeName(b)).toLowerCase().includes(query) ||
            String(getItemName(b.finished_good_id)).toLowerCase().includes(query) ||
            (b.structure_lines || []).some((line) => `${line.description} ${line.remarks || ''}`.toLowerCase().includes(query)) ||
            (b.components || []).some((component) => getItemName(component.item_id).toLowerCase().includes(query))
        );
    }, [boms, items, state.q_boms]);

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-foreground">Manufacturing Control</h1>
                    <p className="text-muted-foreground">MTO Production Management</p>
                </div>
                <div className="flex gap-2">
                    <Button onClick={() => openRecipeModal(null, 'create')} variant="outline">
                        <Plus className="mr-2 h-4 w-4" />
                        New Standard Recipe
                    </Button>
                    <Button onClick={() => setIsWOModalOpen(true)}>
                        <Play className="mr-2 h-4 w-4" />
                        Create Work Order
                    </Button>
                </div>
            </div>

            <div className="bg-muted/30 p-1 rounded-t flex gap-1">
                <button
                    onClick={() => setActiveTab('orders')}
                    className={cn(
                        "px-4 py-2 text-sm font-medium transition-colors rounded-t",
                        activeTab === 'orders'
                            ? "bg-card text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                >
                    Work Orders
                </button>
                <button
                    onClick={() => setActiveTab('tracking')}
                    className={cn(
                        "px-4 py-2 text-sm font-medium transition-colors rounded-t",
                        activeTab === 'tracking'
                            ? "bg-card text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                >
                    Production Tracking
                </button>
                <button
                    onClick={() => setActiveTab('material-issue')}
                    className={cn(
                        "px-4 py-2 text-sm font-medium transition-colors rounded-t",
                        activeTab === 'material-issue'
                            ? "bg-card text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                >
                    Material Issue
                </button>
                <button
                    onClick={() => setActiveTab('shift-entry')}
                    className={cn(
                        "px-4 py-2 text-sm font-medium transition-colors rounded-t",
                        activeTab === 'shift-entry'
                            ? "bg-card text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                >
                    Daily Shift Entry
                </button>
                <button
                    onClick={() => setActiveTab('boms')}
                    className={cn(
                        "px-4 py-2 text-sm font-medium transition-colors rounded-t",
                        activeTab === 'boms'
                            ? "bg-card text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                >
                    Standard Recipes
                </button>
            </div>

            <div className="min-h-[400px]">
                {activeTab === 'orders' ? (
                    <div className="space-y-3">
                        <FlowEntryBanner
                            title="Production Flow Guidance"
                            pathLabel="WO Create -> Start -> Complete"
                            nextHint="Operator view uses WO number and project description; commercial references remain in the audit trail."
                            helpModule="production"
                        />
                        <TableFilterBar
                            searchValue={state.q_orders}
                            onSearchChange={(value) => setFilter('q_orders', value)}
                            searchPlaceholder="Search work orders..."
                            isDirty={isDirty && !!state.q_orders}
                            onClear={() => setFilter('q_orders', '')}
                            rightSlot={`${filteredOrders.length} results`}
                        />
                        <CompactTable data={filteredOrders} columns={woColumns} keyField="wo_id" onRowClick={(row) => navigate(`/manufacturing/orders/${row.wo_id}`)} />
                    </div>
                ) : activeTab === 'tracking' ? (
                    <ProductionTracking />
                ) : activeTab === 'material-issue' ? (
                    <MaterialIssueBoard />
                ) : activeTab === 'shift-entry' ? (
                    <div className="space-y-3">
                        <FlowEntryBanner
                            title="Daily Shift Entry"
                            pathLabel="Manufacturing Control -> Daily Shift Entry"
                            nextHint="Use this screen to post material, labour, installation, finished output, and direct costs across multiple WOs for one date/shift."
                            helpModule="production"
                        />
                        <DailyShiftEntry orders={orders} items={items} onPosted={loadData} />
                    </div>
                ) : (
                    <div className="space-y-3">
                        <TableFilterBar
                            searchValue={state.q_boms}
                            onSearchChange={(value) => setFilter('q_boms', value)}
                            searchPlaceholder="Search recipe name, FG reference, material, sub-part..."
                            isDirty={isDirty && !!state.q_boms}
                            onClear={() => setFilter('q_boms', '')}
                            rightSlot={`${filteredBoms.length} results`}
                        />
                        <CompactTable data={filteredBoms} columns={bomColumns} keyField="id" />
                    </div>
                )}
            </div>

            {isWOModalOpen && (
                <WorkOrderModal
                    isOpen={isWOModalOpen}
                    onClose={() => setIsWOModalOpen(false)}
                    onSuccess={() => { setIsWOModalOpen(false); loadData(); }}
                />
            )}

            {isBOMModalOpen && (
                <BOMEditor
                    isOpen={isBOMModalOpen}
                    onClose={() => { setIsBOMModalOpen(false); setSelectedRecipe(null); }}
                    onSuccess={() => { setIsBOMModalOpen(false); setSelectedRecipe(null); loadData(); }}
                    items={items}
                    initialBom={selectedRecipe}
                    mode={recipeModalMode}
                />
            )}

            {completionTargetId !== null && (
                <CompletionModal
                    isOpen={true}
                    onClose={() => setCompletionTargetId(null)}
                    summary={completionSummary}
                    onConfirm={confirmCompletion}
                />
            )}
        </div>
    );
}
